import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { Label, monkeyPatchChartJsLegend, monkeyPatchChartJsTooltip } from 'ng2-charts';
import {
  SqlAttemptInfo,
  SqlChatbotService,
  SqlChatResponse,
  SqlVisualization,
} from '../shared/sql-chatbot.service';
import { SQL_CHATBOT_FAQS } from './sql-chatbot-faqs';

interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
  aiSummary?: string;
  sql?: string;
  columns?: string[];
  rows?: any[][];
  visualization?: SqlVisualization | null;
  chartType?: ChartType;
  chartLabels?: Label[];
  chartData?: ChartDataSets[];
  chartTitle?: string;
  attempts?: SqlAttemptInfo[];
  success?: boolean;
  showRawData?: boolean;
}

function newSessionId(): string {
  return 'sql-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

@Component({
  selector: 'app-sql-chatbot',
  templateUrl: './sql-chatbot.component.html',
  styleUrls: ['./sql-chatbot.component.scss'],
})
export class SqlChatbotComponent implements OnInit {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;

  messages: ChatMessage[] = [];
  userInput = '';
  isTyping = false;
  showSql = false;
  schemaInfo = '';
  sessionId = newSessionId();

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: true, position: 'bottom' },
    scales: {
      xAxes: [{ gridLines: { display: false } }],
      yAxes: [{ ticks: { beginAtZero: true } }],
    },
  };

  pieChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: true, position: 'bottom' },
  };

  readonly chartColors = [
    '#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706',
    '#dc2626', '#db2777', '#4f46e5', '#0d9488', '#ca8a04',
  ];
  readonly faqs: string[] = SQL_CHATBOT_FAQS;
  faqSearch = '';

  constructor(private sqlService: SqlChatbotService) {
    monkeyPatchChartJsTooltip();
    monkeyPatchChartJsLegend();
  }

  ngOnInit(): void {
    this.messages.push({
      sender: 'bot',
      text: 'Hi! I can query the WebNetram hospital database using natural language. Ask me anything — I only run read-only SELECT queries.',
      timestamp: new Date(),
    });
    this.loadSchemaInfo();
  }

  loadSchemaInfo(): void {
    this.sqlService.getDbStatus().subscribe({
      next: (db) => {
        if (db.connected) {
          this.schemaInfo = `DB connected: ${db.database} @ ${db.server}`;
        } else {
          this.schemaInfo = `DB offline — ${db.error || 'check SQL Server'}`;
        }
      },
      error: () => {
        this.schemaInfo = 'Schema unavailable';
      },
    });
  }

  sendMessage(text?: string): void {
    const msg = (text || this.userInput).trim();
    if (!msg || this.isTyping) return;

    this.userInput = '';
    this.messages.push({ sender: 'user', text: msg, timestamp: new Date() });
    this.isTyping = true;
    this.scrollToBottom();

    this.sqlService.sendMessage(this.sessionId, msg).subscribe({
      next: (res) => this.handleResponse(res),
      error: () => {
        this.isTyping = false;
        this.messages.push({
          sender: 'bot',
          text: 'Sorry, the SQL agent backend is unavailable. Make sure AIBackend is running on port 8000.',
          timestamp: new Date(),
          success: false,
        });
        this.scrollToBottom();
      },
    });
  }

  handleResponse(res: SqlChatResponse): void {
    this.isTyping = false;
    const backendViz = this.prepareVisualization(res.visualization);
    const clientViz = this.buildChartFromRows(res.columns, res.rows);
    const viz =
      clientViz ||
      (backendViz && !this.isIdentifierColumn(backendViz.chartTitle || '') ? backendViz : null) ||
      backendViz;

    const message: ChatMessage = {
      sender: 'bot',
      text: res.response_text,
      aiSummary: res.ai_summary || res.response_text,
      timestamp: new Date(),
      sql: res.sql,
      columns: res.columns,
      rows: res.rows,
      visualization: res.visualization,
      chartType: viz?.chartType,
      chartLabels: viz?.chartLabels,
      chartData: viz?.chartData,
      chartTitle: viz?.chartTitle,
      attempts: res.attempts,
      success: res.success,
      showRawData: true,
    };
    this.messages.push(message);
    this.scrollToBottom();
  }

  hasChart(msg: ChatMessage): boolean {
    return !!(
      msg.success !== false &&
      msg.chartType &&
      msg.chartLabels &&
      msg.chartLabels.length > 0 &&
      msg.chartData &&
      msg.chartData.length > 0 &&
      msg.chartData[0].data &&
      (msg.chartData[0].data as number[]).length > 0
    );
  }

  getChartValues(msg: ChatMessage): number[] {
    const data = msg.chartData && msg.chartData[0] && msg.chartData[0].data;
    return Array.isArray(data) ? (data as number[]) : [];
  }

  chartOptionsFor(msg: ChatMessage): ChartOptions {
    const type = msg.chartType;
    if (type === 'pie' || type === 'doughnut') {
      return this.pieChartOptions;
    }
    return {
      ...this.chartOptions,
      scales: {
        xAxes: [{ gridLines: { display: false } }],
        yAxes: [{ ticks: { beginAtZero: true } }],
      },
    };
  }

  toggleRawData(msg: ChatMessage): void {
    msg.showRawData = !msg.showRawData;
  }

  trackByIndex(index: number): number {
    return index;
  }

  filteredFaqQuestions(): string[] {
    const term = this.faqSearch.trim().toLowerCase();
    if (!term) {
      return this.faqs;
    }
    return this.faqs.filter((q) => q.toLowerCase().includes(term));
  }

  useSuggestion(text: string): void {
    this.sendMessage(text);
  }

  clearChat(): void {
    this.sqlService.resetSession(this.sessionId).subscribe(() => {
      this.sessionId = newSessionId();
      this.messages = [{
        sender: 'bot',
        text: 'Chat cleared. Ask a new question about hospital data.',
        timestamp: new Date(),
      }];
    });
  }

  toggleSql(): void {
    this.showSql = !this.showSql;
  }

  private prepareVisualization(viz?: SqlVisualization | null): {
    chartType: ChartType;
    chartLabels: Label[];
    chartData: ChartDataSets[];
    chartTitle?: string;
  } | null {
    if (!viz || !viz.type || viz.type === 'none') {
      return null;
    }
    const allowed: ChartType[] = ['bar', 'line', 'pie', 'doughnut'];
    const chartType = allowed.includes(viz.type as ChartType) ? (viz.type as ChartType) : 'bar';
    if (!viz.labels?.length || !viz.datasets?.length) {
      return null;
    }
    const values = viz.datasets[0]?.data || [];
    if (!values.length || values.every((v) => v === 0)) {
      return null;
    }
    return {
      chartType,
      chartLabels: viz.labels,
      chartTitle: viz.title,
      chartData: viz.datasets.map((ds) => ({
        label: ds.label,
        data: ds.data,
        backgroundColor: this.colorsForChart(chartType, ds.data.length),
        borderColor: '#ffffff',
        borderWidth: 1,
      })),
    };
  }

  private buildChartFromRows(columns?: string[], rows?: any[][]): {
    chartType: ChartType;
    chartLabels: Label[];
    chartData: ChartDataSets[];
    chartTitle?: string;
  } | null {
    if (!columns?.length || !rows?.length) {
      return null;
    }

    if (rows.length === 1 && columns.length === 1) {
      const value = this.parseNumber(columns[0], rows[0][0]);
      if (value === null) {
        return null;
      }
      const label = columns[0];
      return this.makeBarChart([label], [value], label, label);
    }

    if (rows.length === 1 && columns.length > 1) {
      const labels: string[] = [];
      const values: number[] = [];
      columns.forEach((col, i) => {
        const value = this.parseNumber(col, rows[0][i]);
        if (value !== null) {
          labels.push(col);
          values.push(value);
        }
      });
      if (values.length >= 1) {
        return this.makeBarChart(labels, values, 'Breakdown', 'Value');
      }
    }

    const countChart = this.buildCountChart(columns, rows);
    if (countChart) {
      return countChart;
    }

    const numericIndices = columns
      .map((col, i) => i)
      .filter((i) => this.isMeaningfulMeasure(columns[i], rows, i));
    if (!numericIndices.length) {
      return null;
    }

    const valueIdx = numericIndices[0];
    const labelIdx = columns.findIndex(
      (_, i) => i !== valueIdx && !this.isIdentifierColumn(columns[i]) && !this.isDateColumn(columns[i]),
    );
    if (labelIdx < 0) {
      return null;
    }

    const labels: string[] = [];
    const values: number[] = [];
    for (const row of rows.slice(0, 15)) {
      const label = this.rowLabel(row, columns, labelIdx);
      const value = this.parseNumber(columns[valueIdx], row[valueIdx]);
      if (label && value !== null) {
        labels.push(label);
        values.push(value);
      }
    }

    if (labels.length < 2) {
      return this.buildCountChart(columns, rows);
    }

    const chartType: ChartType = this.looksLikeDates(labels) ? 'line' : 'bar';
    return this.makeSeriesChart(chartType, labels, values, columns[valueIdx], columns[valueIdx]);
  }

  private buildCountChart(columns: string[], rows: any[][]): {
    chartType: ChartType;
    chartLabels: Label[];
    chartData: ChartDataSets[];
    chartTitle?: string;
  } | null {
    const categoryIdx = this.findCategoryColumn(columns);
    const categoryChart =
      categoryIdx >= 0 ? this.buildCountForColumn(columns, rows, categoryIdx) : null;

    const dateIdx = columns.findIndex((col) => this.isDateColumn(col));
    if (dateIdx >= 0) {
      const dateChart = this.buildCountForColumn(columns, rows, dateIdx, true);
      const dateBuckets = dateChart ? dateChart.chartLabels.length : 0;

      if (categoryChart && categoryChart.chartLabels.length >= 2) {
        return categoryChart;
      }
      if (dateChart && dateBuckets >= 4) {
        return dateChart;
      }
      if (categoryChart) {
        return categoryChart;
      }
      if (dateChart) {
        return dateChart;
      }
    }

    if (categoryChart) {
      return categoryChart;
    }

    const labelIdx = this.findLabelColumn(columns);
    if (labelIdx < 0) {
      return null;
    }
    return this.buildCountForColumn(columns, rows, labelIdx);
  }

  private buildCountForColumn(
    columns: string[],
    rows: any[][],
    colIdx: number,
    isDate = false,
  ): {
    chartType: ChartType;
    chartLabels: Label[];
    chartData: ChartDataSets[];
    chartTitle?: string;
  } | null {
    const counts = new Map<string, number>();
    for (const row of rows) {
      const key = isDate
        ? this.formatDateLabel(row[colIdx])
        : this.rowLabel(row, columns, colIdx);
      if (key) {
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    if (!counts.size) {
      return null;
    }

    const sorted = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, count]) => count);
    const chartType: ChartType = 'bar';

    return this.makeSeriesChart(
      chartType,
      labels,
      values,
      `Count by ${columns[colIdx]}`,
      'Count',
    );
  }

  private findCategoryColumn(columns: string[]): number {
    const patterns = [/doctor/i, /service/i, /department/i, /branch/i, /category/i, /patient/i, /name/i];
    for (const pattern of patterns) {
      const idx = columns.findIndex(
        (col) => pattern.test(col) && !this.isIdentifierColumn(col) && !this.isDateColumn(col),
      );
      if (idx >= 0) {
        return idx;
      }
    }
    return -1;
  }

  private makeBarChart(
    labels: string[],
    values: number[],
    title: string,
    seriesLabel: string,
  ) {
    return this.makeSeriesChart('bar', labels, values, title, seriesLabel);
  }

  private makeSeriesChart(
    chartType: ChartType,
    labels: string[],
    values: number[],
    title: string,
    seriesLabel: string,
  ) {
    const safeType: ChartType = chartType === 'line' && labels.length < 2 ? 'bar' : chartType;
    return {
      chartType: safeType,
      chartLabels: labels,
      chartTitle: title,
      chartData: [{
        label: seriesLabel,
        data: values,
        backgroundColor: safeType === 'line'
          ? 'rgba(37, 99, 235, 0.2)'
          : this.colorsForChart('bar', values.length),
        borderColor: safeType === 'line' ? '#2563eb' : '#ffffff',
        borderWidth: safeType === 'line' ? 2 : 1,
        fill: safeType === 'line',
        hoverBackgroundColor: safeType === 'line' ? '#2563eb' : undefined,
      }],
    };
  }

  private findLabelColumn(columns: string[]): number {
    const preferred = columns.findIndex(
      (col) => /firstname|patient|doctor|service|department|branch|category|name/i.test(col) &&
        !this.isIdentifierColumn(col),
    );
    if (preferred >= 0) {
      return preferred;
    }
    return columns.findIndex(
      (col) => !this.isIdentifierColumn(col) && !this.isDateColumn(col),
    );
  }

  private rowLabel(row: any[], columns: string[], labelIdx: number): string {
    const first = this.formatChartLabel(row[labelIdx]);
    if (labelIdx + 1 < columns.length && /last.?name|surname/i.test(columns[labelIdx + 1])) {
      const last = this.formatChartLabel(row[labelIdx + 1]);
      return [first, last].filter(Boolean).join(' ').trim();
    }
    return first;
  }

  private formatDateLabel(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    const match = text.match(/(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : this.formatChartLabel(value);
  }

  private isDateColumn(column: string): boolean {
    return /date|time|month|year|day/i.test(column);
  }

  private isIdentifierColumn(column: string): boolean {
    return /mobile|phone|contact|regno|uhid|billno|receipt|email|pincode|zip|aadhar|mrn|pk_/i.test(column);
  }

  private isMeaningfulMeasure(column: string, rows: any[][], colIdx: number): boolean {
    if (this.isIdentifierColumn(column)) {
      return false;
    }
    const numbers = rows
      .map((row) => this.parseNumber(column, row[colIdx]))
      .filter((value): value is number => value !== null);
    if (!numbers.length) {
      return false;
    }
    return !numbers.every((value) => Math.abs(value) >= 1_000_000_000 && Number.isInteger(value));
  }

  private parseNumber(column: string, value: any): number | null {
    if (this.isIdentifierColumn(column)) {
      return null;
    }
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    const parsed = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(parsed)) {
      return null;
    }
    if (Math.abs(parsed) >= 1_000_000_000 && Number.isInteger(parsed)) {
      return null;
    }
    return parsed;
  }

  private colorsForChart(chartType: ChartType, count: number): string | string[] {
    if (chartType === 'line') {
      return 'rgba(37, 99, 235, 0.7)';
    }
    return Array.from({ length: Math.max(count, 1) }, (_, i) => this.chartColors[i % this.chartColors.length]);
  }

  private formatChartLabel(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text.length > 40 ? `${text.slice(0, 40)}…` : text;
  }

  private looksLikeDates(labels: string[]): boolean {
    const pattern = /\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}/;
    const hits = labels.filter((label) => pattern.test(label)).length;
    return hits >= labels.length * 0.6;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }
}
